<template>
  <div class="page-auth">
    <div class="login-page">
      <div
        class="hidden bg-transparent lg:flex flex-col items-center justify-center"
      >
        <p class="text-white text-4xl text-center w-5/6">
          Activation de votre compte SSO
        </p>
      </div>
      <div
        class="content-form flex flex-col items-center justify-center bg-transparent h-full"
      >
        <div
          class="mx-auto grid w-4/5 md:w-3/4 gap-6 bg-white py-8 px-3 rounded-15"
        >
          <div class="grid gap-2 text-center justify-items-center items-center">
            <h1 class="text-4xl font-[500] text-spect">Activation SSO</h1>
            <p class="text-balance">
              Complétez votre profil pour activer l'authentification SSO
            </p>
          </div>

          <form @submit.prevent="handleSubmit" class="grid gap-3 px-4">
            <!-- Error Display -->
            <div
              v-if="errorMessage"
              class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded"
            >
              <p class="font-medium">{{ errorMessage }}</p>
              <ul
                v-if="fieldErrors && fieldErrors.length > 0"
                class="mt-2 list-disc list-inside"
              >
                <li v-for="(error, index) in fieldErrors" :key="index">
                  {{ error }}
                </li>
              </ul>
            </div>

            <!-- Success Message Display -->
            <div
              v-if="successMessage"
              class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded"
            >
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <p class="font-medium">{{ successMessage }}</p>
              </div>
            </div>
            <!-- Organisation Selection / Readonly -->
            <div>
              <label class="block font-medium text-left text-gray-700 mb-1"
                >Organisation</label
              >
              <template v-if="isOrgLocked">
                <input
                  type="text"
                  :value="organisationName"
                  class="w-full px-4 py-2 input input-bordered ui-input"
                  readonly
                  disabled
                />
              </template>
              <template v-else>
                <select
                  v-model="formData.organisation"
                  class="w-full px-4 py-2 input input-bordered ui-input"
                  required
                  @change="onOrganisationChange"
                >
                  <option value="" disabled>
                    Sélectionnez votre organisation
                  </option>
                  <option
                    v-for="org in organisationOptions"
                    :key="org.id"
                    :value="org.id"
                  >
                    {{ org.name }}
                  </option>
                </select>
              </template>
            </div>

            <!-- Service Selection -->
            <div>
              <label class="block font-medium text-left text-gray-700 mb-1"
                >Service</label
              >
              <select
                v-model="formData.service"
                class="w-full px-4 py-2 input input-bordered ui-input"
                required
              >
                <option value="" disabled>Sélectionnez votre service</option>
                <option
                  v-for="service in serviceOptions"
                  :key="
                    service?.id || service?.uuid || service?.value || service
                  "
                  :value="
                    service?.id || service?.uuid || service?.value || service
                  "
                >
                  {{
                    service?.name ||
                    service?.title ||
                    service?.label ||
                    service?.display_name ||
                    service
                  }}
                </option>
              </select>
              <p
                v-if="!isServicesLoading && serviceOptions.length === 0"
                class="text-sm text-gray-500 mt-1"
              >
                Aucun service disponible pour cette organisation.
              </p>
            </div>

            <!-- First Name / Last Name -->
            <div class="grid md:grid-cols-2 gap-3">
              <div>
                <label class="block font-medium text-left text-gray-700 mb-1"
                  >Prénom</label
                >
                <input
                  type="text"
                  v-model="formData.firstname"
                  class="w-full px-4 py-2 input input-bordered ui-input"
                  required
                  placeholder="Votre prénom"
                />
              </div>
              <div>
                <label class="block font-medium text-left text-gray-700 mb-1"
                  >Nom</label
                >
                <input
                  type="text"
                  v-model="formData.lastname"
                  class="w-full px-4 py-2 input input-bordered ui-input"
                  required
                  placeholder="Votre nom"
                />
              </div>
            </div>

            <!-- Domain Selection -->
            <div v-if="domains.length > 0">
              <label class="block font-medium text-left text-gray-700 mb-1"
                >Domaine de votre organisation</label
              >
              <select
                v-model="formData.domain"
                class="w-full px-4 py-2 input input-bordered ui-input"
                required
              >
                <option value="" disabled>Sélectionnez votre domaine</option>
                <option
                  v-for="domain in domains"
                  :key="domain.id || domain.domain"
                  :value="domain.domain || domain"
                >
                  {{ domain.domain || domain }}
                </option>
              </select>
            </div>

            <!-- Country / City -->
            <div class="grid md:grid-cols-2 gap-3">
              <div>
                <label class="block font-medium text-left text-gray-700 mb-1"
                  >Pays</label
                >
                <div class="input input-bordered ui-input">
                  <country
                    class="w-full"
                    @country="changeCountry"
                    id="country-input"
                  />
                </div>
              </div>
              <div>
                <div>
                  <label class="block font-medium text-left text-gray-700 mb-1"
                    >Ville</label
                  >
                  <div
                    v-if="cityOptions && cityOptions.length > 0"
                    class="input input-bordered ui-input px-0"
                  >
                    <city-dropdown
                      class="w-full px-4"
                      :cities="cityOptions"
                      v-model="formData.city"
                    />
                  </div>
                  <div v-else>
                    <input
                      type="text"
                      v-model="formData.city"
                      class="w-full px-4 py-2 input input-bordered ui-input"
                      required
                      placeholder="Votre ville"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label class="block font-medium text-left text-gray-700 mb-1"
                >Langue</label
              >
              <select
                v-model="formData.language"
                class="w-full px-4 py-2 input input-bordered ui-input"
                required
              >
                <option value="" disabled>Sélectionnez une langue</option>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>

            <!-- Phone with Country Code -->
            <div>
              <label class="block font-medium text-left text-gray-700 mb-1"
                >Téléphone</label
              >
              <div class="relative">
                <country-code
                  class="country-dial absolute top-0 pl-2 country-input"
                  @code="changeCode"
                  :dial="formData.dialCode"
                />
                <input
                  type="tel"
                  v-model="formData.phone"
                  class="w-full pl-auto input input-bordered ui-input tel-input"
                  required
                  placeholder="Numéro de téléphone"
                />
              </div>
            </div>

            <div class="mt-4">
              <button
                type="submit"
                class="w-full flex items-center justify-center gap-3 bg-spect text-white py-3 rounded-6 hover:bg-opacity-90 transition-colors btn-disable"
                :disabled="isSubmitting || !isFormValid"
              >
                <span
                  v-if="isSubmitting"
                  class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                ></span>
                <span> Activer </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAPI } from "@/axios-api.js";
import country from "../../components/shared/input/country.vue";
import CityDropdown from "../../components/shared/input/city.vue";
import countryCode from "../../components/shared/input/countryCode.vue";
import { City } from "country-state-city";

export default {
  name: "SSOActivate",
  components: {
    country,
    countryCode,
    CityDropdown,
  },
  data() {
    return {
      isSubmitting: false,
      isOrgLocked: false,
      organisationName: "",
      errorMessage: "",
      successMessage: "",
      fieldErrors: [],
      formData: {
        organisation: "",
        service: "",
        domain: "",
        email: "",
        firstname: "",
        lastname: "",
        phone: "",
        countryCode: "+33",
        dialCode: "33",
        country: "",
        city: "",
        language: "",
        otpMethod: "email",
      },
      organisationOptions: [],
      serviceOptions: [],
      cityOptions: [],
      selectedCountryIso: "",
      isServicesLoading: false,
      domains: [],
      isFormValid: true,
      accessTokenFromUrl: "",
      refreshTokenFromUrl: "",
      userDataFromUrl: null,
    };
  },
  methods: {
    changeCountry(country) {
      this.formData.dialCode = country[2];
      this.formData.countryCode = "+" + country[2];
      this.formData.country = country[0];
      this.selectedCountryIso = country[1];
      this.fetchCities();
    },
    changeCode(country) {
      this.formData.dialCode = country[2];
      this.formData.countryCode = "+" + country[2];
    },

    fetchCities() {
      if (this.selectedCountryIso) {
        try {
          const cities = City.getCitiesOfCountry(
            this.selectedCountryIso.toUpperCase(),
          );
          this.cityOptions = cities || [];
          this.formData.city = "";
        } catch (e) {
          console.error("Error fetching cities", e);
          this.cityOptions = [];
        }
      } else {
        this.cityOptions = [];
      }
    },

    async handleSubmit() {
      // Clear previous errors and success messages
      this.errorMessage = "";
      this.successMessage = "";
      this.fieldErrors = [];

      if (
        !this.isFormValid ||
        !this.formData.countryCode ||
        !this.formData.phone ||
        !this.formData.service ||
        !this.formData.language ||
        !this.formData.firstname ||
        !this.formData.lastname
      ) {
        this.errorMessage = "Veuillez renseigner tous les champs requis";
        return;
      }

      this.isSubmitting = true;
      try {
        const payload = {
          firstname: this.formData.firstname,
          lastname: this.formData.lastname,
          country: this.formData.country,
          city: this.formData.city,
          phone: this.formData.phone,
          code_phone: this.formData.countryCode,
          service: this.formData.service,
          language: this.formData.language,
          otp_method: this.formData.otpMethod,
        };
        const token = localStorage.getItem("token") || this.accessTokenFromUrl;
        const response = await getAPI.put(`sso/activate-user/`, payload, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        });

        if (response.status === 200) {
          // Save tokens and user data to localStorage after successful activation
          if (this.accessTokenFromUrl && !localStorage.getItem("token")) {
            localStorage.setItem("token", this.accessTokenFromUrl);
          }
          if (
            this.refreshTokenFromUrl &&
            !localStorage.getItem("refresh_token")
          ) {
            localStorage.setItem("refresh_token", this.refreshTokenFromUrl);
          }

          // Save user data if available from URL
          if (this.userDataFromUrl) {
            if (this.userDataFromUrl.id)
              localStorage.setItem("user", this.userDataFromUrl.id);
            if (this.userDataFromUrl.firstname)
              localStorage.setItem("firstname", this.userDataFromUrl.firstname);
            if (this.userDataFromUrl.lastname)
              localStorage.setItem("lastname", this.userDataFromUrl.lastname);
            if (this.userDataFromUrl.type_account)
              localStorage.setItem(
                "type_account",
                this.userDataFromUrl.type_account,
              );
            if (this.userDataFromUrl.role)
              localStorage.setItem("role", this.userDataFromUrl.role);
            if (this.userDataFromUrl.organisation)
              localStorage.setItem(
                "organisation",
                this.userDataFromUrl.organisation,
              );
            if (this.userDataFromUrl.code_organisation)
              localStorage.setItem(
                "code_organisation",
                this.userDataFromUrl.code_organisation,
              );
            if (this.userDataFromUrl.service)
              localStorage.setItem("service", this.userDataFromUrl.service);
          }

          // Also save any user data from response if available
          if (response.data && response.data.user) {
            const userData = response.data.user;
            if (userData.id) localStorage.setItem("user", userData.id);
            if (userData.firstname)
              localStorage.setItem("firstname", userData.firstname);
            if (userData.lastname)
              localStorage.setItem("lastname", userData.lastname);
            if (userData.type_account)
              localStorage.setItem("type_account", userData.type_account);
            if (userData.role) localStorage.setItem("role", userData.role);
            if (userData.organisation)
              localStorage.setItem("organisation", userData.organisation);
            if (userData.code_organisation)
              localStorage.setItem(
                "code_organisation",
                userData.code_organisation,
              );
            if (userData.service)
              localStorage.setItem("service", userData.service);
          }

          // Save tokens from response if available
          if (response.data && response.data.access) {
            localStorage.setItem("token", response.data.access);
          }
          if (response.data && response.data.refresh) {
            localStorage.setItem("refresh_token", response.data.refresh);
          }

          this.successMessage =
            "Compte SSO activé avec succès! Redirection vers le tableau de bord...";
          this.isSubmitting = false;

          setTimeout(() => {
            this.$router.push("/dashboard");
          }, 2500);
        } else {
          this.errorMessage = "Erreur lors de l'activation du compte";
          this.isSubmitting = false;
        }
      } catch (error) {
        // Handle error response
        if (error.response) {
          const errorData = error.response.data;

          this.errorMessage =
            errorData?.message ||
            errorData?.detail ||
            errorData?.error ||
            "Erreur lors de l'activation";

          this.fieldErrors = [];
          if (errorData && typeof errorData === "object") {
            Object.keys(errorData).forEach((key) => {
              if (key !== "message" && key !== "detail" && key !== "error") {
                const fieldError = errorData[key];
                if (Array.isArray(fieldError)) {
                  fieldError.forEach((err) => {
                    this.fieldErrors.push(`${key}: ${err}`);
                  });
                } else if (typeof fieldError === "string") {
                  this.fieldErrors.push(`${key}: ${fieldError}`);
                }
              }
            });
          }
        } else if (error.request) {
          this.errorMessage =
            "Erreur de connexion. Vérifiez votre connexion internet et réessayez.";
        } else {
          this.errorMessage = error?.message || "Erreur lors de l'activation";
        }
        this.isSubmitting = false;
      }
    },
    async loadOrganisations() {
      try {
        // Use token from URL if available, otherwise use localStorage token
        const token = this.accessTokenFromUrl || localStorage.getItem("token");
        const res = await getAPI.get("organisations/", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (res.status === 200) {
          this.organisationOptions = res.data?.results || res.data || [];
        }
      } catch (e) {
        console.warn("Unable to load organisations", e);
      }
    },
    async onOrganisationChange() {
      this.domains = [];
      this.serviceOptions = [];
      this.formData.service = "";
      const organisationId = this.formData.organisation;
      if (!organisationId) {
        this.organisationName = "";
        // If no organisation selected, load all services
        await this.fetchServices(null);
        return;
      }

      // Fetch organisation details to get name and domains
      // Use token from URL if available, otherwise use localStorage token
      const token = this.accessTokenFromUrl || localStorage.getItem("token");
      try {
        const org = await getAPI.get(`organisations/${organisationId}/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (org.data) {
          const access = org.data?.access_domains || [];
          this.organisationName =
            org.data?.name || org.data?.title || organisationId;
          this.domains = Array.isArray(access) ? access : [];
        }
      } catch (err) {
        console.warn("Unable to load organisation details", err);
        // Set a fallback name if API fails
        this.organisationName = organisationId;
      }

      // Fetch services for this organisation
      await this.fetchServices(organisationId);
    },
    async fetchServices(organisationId) {
      this.isServicesLoading = true;
      try {
        const apiUrl = organisationId
          ? `services/?organisation=${organisationId}`
          : "services/";
        console.log("Fetching services from:", apiUrl);
        // Use token from URL if available, otherwise use localStorage token
        const token = this.accessTokenFromUrl || localStorage.getItem("token");
        const services = await getAPI.get(apiUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        const payload =
          services.data?.results || services.data?.data || services.data || [];
        this.serviceOptions = Array.isArray(payload) ? payload : [];
        console.log(
          "Loaded services:",
          this.serviceOptions.length,
          this.serviceOptions,
        );

        // Validate and set service if already selected
        if (this.formData.service) {
          const resolveServiceId = (service) => {
            if (!service || typeof service !== "object") return service;
            return (
              service.id ??
              service.uuid ??
              service.value ??
              service.code ??
              service.pk ??
              service.slug ??
              service
            );
          };
          const currentId = this.formData.service;
          const exists = this.serviceOptions.some((svc) => {
            const svcId = resolveServiceId(svc);
            return svcId && String(svcId) === String(currentId);
          });
          if (!exists) {
            this.formData.service = "";
          }
        }

        // Try to pre-select service from callback if available
        if (
          !this.formData.service &&
          this.userDataFromUrl &&
          this.userDataFromUrl.service
        ) {
          const resolveServiceId = (service) => {
            if (!service || typeof service !== "object") return service;
            return (
              service.id ??
              service.uuid ??
              service.value ??
              service.code ??
              service.pk ??
              service.slug ??
              service
            );
          };
          const matchedService = this.serviceOptions.find((svc) => {
            const svcId = resolveServiceId(svc);
            return (
              svcId && String(svcId) === String(this.userDataFromUrl.service)
            );
          });
          if (matchedService) {
            this.formData.service = resolveServiceId(matchedService);
          }
        }
      } catch (err) {
        console.error("Unable to load services", err);
        this.serviceOptions = [];
        this.formData.service = "";
      } finally {
        this.isServicesLoading = false;
      }
    },
    showAlert(msg, type) {
      this.$emit("alert", msg, type);
    },
  },
  async mounted() {
    const params = new URLSearchParams(window.location.search);

    // Extract tokens from URL but DO NOT save to localStorage yet - only store in component data
    const accessFromUrl = params.get("access") || params.get("access_token");
    if (accessFromUrl) {
      this.accessTokenFromUrl = accessFromUrl;
    }

    const refreshFromUrl = params.get("refresh") || params.get("refresh_token");
    if (refreshFromUrl) {
      this.refreshTokenFromUrl = refreshFromUrl;
    }

    // Extract email from URL and prefill form (but don't save to localStorage)
    const email = params.get("email");
    if (email) {
      this.formData.email = decodeURIComponent(email);
    }

    // Extract and parse user data from callback user param
    const userParam = params.get("user");
    let orgFromCallback = null;

    if (userParam) {
      try {
        const decoded = decodeURIComponent(userParam);
        // Parse user data - handle Python dict format
        let userData = {};
        try {
          // Replace Python None with null, True/False with true/false
          let cleaned = decoded.replace(/\bNone\b/g, "null");
          cleaned = cleaned.replace(/\bTrue\b/g, "true");
          cleaned = cleaned.replace(/\bFalse\b/g, "false");
          // Replace single quotes with double quotes for JSON parsing
          cleaned = cleaned.replace(/'/g, '"');
          // Replace UUID() with just the string value
          cleaned = cleaned.replace(/UUID\(['"]([^'"]+)['"]\)/g, '"$1"');

          userData = JSON.parse(cleaned);
        } catch (jsonError) {
          // If JSON parsing fails, try to extract values manually
          console.log("JSON parsing failed, trying manual extraction...");
          userData = {};

          // Extract UUID values
          const idMatch = decoded.match(/UUID\(['"]([^'"]+)['"]\)/);
          if (idMatch) userData.id = idMatch[1];

          // Extract string values
          const firstnameMatch = decoded.match(
            /['"]firstname['"]:\s*['"]([^'"]+)['"]/,
          );
          if (firstnameMatch) userData.firstname = firstnameMatch[1];

          const lastnameMatch = decoded.match(
            /['"]lastname['"]:\s*['"]([^'"]+)['"]/,
          );
          if (lastnameMatch) userData.lastname = lastnameMatch[1];

          const typeAccountMatch = decoded.match(
            /['"]type_account['"]:\s*['"]([^'"]+)['"]/,
          );
          if (typeAccountMatch) userData.type_account = typeAccountMatch[1];

          const roleMatch = decoded.match(/['"]role['"]:\s*['"]([^'"]+)['"]/);
          if (roleMatch) userData.role = roleMatch[1];

          // Handle service: None case
          const serviceMatch = decoded.match(
            /['"]service['"]:\s*(None|['"]([^'"]+)['"])/,
          );
          if (serviceMatch && serviceMatch[2]) {
            userData.service = serviceMatch[2];
          }

          const orgMatch = decoded.match(
            /['"]organisation['"]:\s*UUID\(['"]([^'"]+)['"]\)/,
          );
          if (orgMatch) {
            userData.organisation = orgMatch[1];
            orgFromCallback = orgMatch[1];
          }

          const codeOrgMatch = decoded.match(
            /['"]code_organisation['"]:\s*['"]([^'"]+)['"]/,
          );
          if (codeOrgMatch) userData.code_organisation = codeOrgMatch[1];

          const isCompletedMatch = decoded.match(
            /['"]is_completed['"]:\s*(True|False)/,
          );
          if (isCompletedMatch)
            userData.is_completed = isCompletedMatch[1] === "True";
        }

        this.userDataFromUrl = userData;

        // Prefill form fields from user data (but don't save to localStorage)
        if (userData.firstname) {
          this.formData.firstname = userData.firstname;
        }
        if (userData.lastname) {
          this.formData.lastname = userData.lastname;
        }

        // Handle organisation from callback
        if (userData.organisation) {
          orgFromCallback = userData.organisation;
        }
      } catch (e) {
        console.warn("Unable to parse user data from callback", e);
      }
    }

    // Handle name parameter as fallback for firstname/lastname
    const nameParam = params.get("name");
    if (nameParam && (!this.formData.firstname || !this.formData.lastname)) {
      const decodedName = decodeURIComponent(nameParam);
      const nameParts = decodedName.split(" ");
      if (nameParts.length > 0 && !this.formData.firstname) {
        this.formData.firstname = nameParts[0];
      }
      if (nameParts.length > 1 && !this.formData.lastname) {
        this.formData.lastname = nameParts.slice(1).join(" ");
      } else if (nameParts.length === 1 && !this.formData.lastname) {
        this.formData.lastname = "";
      }
    }

    // Load organisation and services
    const orgFromStorage =
      orgFromCallback || localStorage.getItem("organisation");
    if (orgFromStorage) {
      this.formData.organisation = orgFromStorage;
      this.isOrgLocked = true;
      // Load organisation details and services
      await this.onOrganisationChange();
    } else {
      // If no organisation from callback, load all organisations
      await this.loadOrganisations();
      // Load all services if no organisation is selected
      await this.fetchServices(null);
    }
  },
};
</script>

<style scoped lang="scss">
@use "../../assets/scss/tailwinds.scss";
</style>
